/**
 * Utility functions for handling filter preservation across navigation
 */

/**
 * Extracts current filter values from keyRequest and returns encoded filter string
 * @param {Array} keyRequest - The keyRequest array from TableBlock
 * @param {number|null} currentPage - Current page number (optional)
 * @returns {string|null} - Encoded filter values or null if no filters
 */
export const getFilterValuesFromKeyRequest = (
  keyRequest,
  currentPage = null
) => {
  // Handle case where keyRequest is undefined or doesn't have the expected structure
  if (!keyRequest || !Array.isArray(keyRequest) || !keyRequest[1]) {
    return null
  }

  const currentFilters = keyRequest[1]?.filter || {}
  const currentTabFilters = keyRequest[1]?.tabFilter || {}
  const allFilters = { ...currentFilters, ...currentTabFilters }

  // Include page number if provided
  if (currentPage !== null && currentPage !== undefined) {
    allFilters._page = currentPage
  }

  return Object.keys(allFilters).length > 0
    ? encodeURIComponent(JSON.stringify(allFilters))
    : null
}

/**
 * Gets filter values from current URL parameters
 * @returns {string|null} - Filter values from URL or null if not present
 */
export const getFilterValuesFromURL = () => {
  const queryParams = new URLSearchParams(window.location.search)
  return queryParams.get('filterValues')
}

/**
 * Creates a URL with filter values if they exist
 * @param {string} baseUrl - The base URL without query parameters
 * @param {string|null} filterValues - Filter values to append
 * @returns {string} - URL with filter values if they exist
 */
export const createUrlWithFilters = (baseUrl, filterValues) => {
  return filterValues ? `${baseUrl}?filterValues=${filterValues}` : baseUrl
}

/**
 * Creates a URL with filter values from keyRequest
 * @param {string} baseUrl - The base URL without query parameters
 * @param {Array} keyRequest - The keyRequest array from TableBlock
 * @param {number|null} currentPage - Current page number (optional)
 * @returns {string} - URL with filter values if they exist
 */
export const createUrlWithFiltersFromKeyRequest = (
  baseUrl,
  keyRequest,
  currentPage = null
) => {
  const filterValues = getFilterValuesFromKeyRequest(keyRequest, currentPage)
  return createUrlWithFilters(baseUrl, filterValues)
}

/**
 * Creates a URL with filter values from current URL
 * @param {string} baseUrl - The base URL without query parameters
 * @returns {string} - URL with filter values if they exist
 */
export const createUrlWithFiltersFromCurrentURL = (baseUrl) => {
  const filterValues = getFilterValuesFromURL()
  return createUrlWithFilters(baseUrl, filterValues)
}

/**
 * Client-specific filter keys that should not be applied to orders
 */
const CLIENT_FILTER_KEYS = [
  'name',
  'clientID',
  'wechatID',
  'phone',
  'store',
  'clientTags',
  'clientInsights',
]

/**
 * Separates client filters from all filters
 * @param {Object} allFilters - All filter values
 * @returns {Object} - Object with clientFilters and orderFilters
 */
export const separateClientFilters = (allFilters) => {
  const clientFilters = {}
  const orderFilters = {}

  Object.keys(allFilters).forEach((key) => {
    // Preserve page number in both filters
    if (key === '_page') {
      clientFilters[key] = allFilters[key]
      orderFilters[key] = allFilters[key]
    } else if (CLIENT_FILTER_KEYS.includes(key)) {
      clientFilters[key] = allFilters[key]
    } else {
      orderFilters[key] = allFilters[key]
    }
  })

  return { clientFilters, orderFilters }
}

/**
 * Creates a URL with client filters stored separately
 * @param {string} baseUrl - The base URL without query parameters
 * @param {Array} keyRequest - The keyRequest array from TableBlock
 * @param {number|null} currentPage - Current page number (optional)
 * @returns {string} - URL with clientFilters and filterValues (for orders) parameters
 */
export const createUrlWithSeparatedFilters = (
  baseUrl,
  keyRequest,
  currentPage = null
) => {
  const allFilterValues = getFilterValuesFromKeyRequest(keyRequest, currentPage)
  if (!allFilterValues) return baseUrl

  try {
    const allFilters = JSON.parse(decodeURIComponent(allFilterValues))
    const { clientFilters, orderFilters } = separateClientFilters(allFilters)

    const params = new URLSearchParams()
    if (Object.keys(clientFilters).length > 0) {
      params.set(
        'clientFilters',
        encodeURIComponent(JSON.stringify(clientFilters))
      )
    }
    if (Object.keys(orderFilters).length > 0) {
      params.set(
        'filterValues',
        encodeURIComponent(JSON.stringify(orderFilters))
      )
    }

    const queryString = params.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  } catch (e) {
    console.error('Error separating filters:', e)
    return baseUrl
  }
}

/**
 * Gets client filters from URL parameters
 * @returns {string|null} - Client filter values from URL or null if not present
 */
export const getClientFiltersFromURL = () => {
  const queryParams = new URLSearchParams(window.location.search)
  return queryParams.get('clientFilters')
}

/**
 * Creates a URL with client filters restored from URL
 * @param {string} baseUrl - The base URL without query parameters
 * @returns {string} - URL with clientFilters restored if they exist
 */
export const createUrlWithRestoredClientFilters = (baseUrl) => {
  const clientFilters = getClientFiltersFromURL()
  if (!clientFilters) return baseUrl

  const params = new URLSearchParams()
  params.set('filterValues', clientFilters)
  return `${baseUrl}?${params.toString()}`
}
